import { PreviewCard } from '@base-ui/react/preview-card';
import { styled } from '@conteg/ui';
import { useTranslation } from 'react-i18next';

const PreviewTrigger = styled(PreviewCard.Trigger)`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary?.blue500 || '#3b82f6'};
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.primary?.blue600 || '#2563eb'};
  }
`;

const PreviewPopup = styled(PreviewCard.Popup)`
  background: white;
  border: 1px solid
    ${({ theme }) => theme.colors.secondary?.grey300 || '#d1d5db'};
  border-radius: 1rem;
  padding: 4rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 3.5rem;
  line-height: 1.6;
  z-index: 1000;
  transform-origin: var(--transform-origin);
  transition:
    transform 150ms,
    opacity 150ms;

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transform: scale(0.9);
  }
`;

const Arrow = styled(PreviewCard.Arrow)`
  display: flex;

  &[data-side='top'] {
    bottom: -7px;
    rotate: 180deg;
  }

  &[data-side='bottom'] {
    top: -7px;
    rotate: 0deg;
  }

  &[data-side='left'] {
    right: -13px;
    rotate: 90deg;
  }

  &[data-side='right'] {
    left: -13px;
    rotate: -90deg;
  }

  .arrow-fill {
    fill: canvas;
  }

  .arrow-outer-stroke {
    @media (prefers-color-scheme: light) {
      fill: ${({ theme }) => theme.colors.secondary?.grey300 || '#d1d5db'};
    }
  }

  .arrow-inner-stroke {
    @media (prefers-color-scheme: dark) {
      fill: ${({ theme }) => theme.colors.secondary?.grey300 || '#d1d5db'};
    }
  }
`;

interface DetailPreviewCardProps {
  label?: string;
  content: string;
}

function ArrowSvg(props: React.ComponentProps<'svg'>) {
  return (
    <svg width="20" height="10" viewBox="0 0 20 10" fill="none" {...props}>
      <path
        className="arrow-fill"
        d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
      />
      <path
        className="arrow-outer-stroke"
        d="M8.99542 1.85876C9.75604 1.17425 10.9106 1.17422 11.6713 1.85878L16.5281 6.22989C17.0789 6.72568 17.7938 7.00001 18.5349 7.00001L15.89 7L11.0023 2.60207C10.622 2.2598 10.0447 2.2598 9.66436 2.60207L4.77734 7L2.13171 7.00001C2.87284 7.00001 3.58774 6.72568 4.13861 6.22989L8.99542 1.85876Z"
      />
      <path
        className="arrow-inner-stroke"
        d="M10.3333 3.34539L5.47654 7.71648C4.55842 8.54279 3.36693 9 2.13172 9H0V8H2.13172C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8H20V9H18.5349C17.2998 9 16.1083 8.54278 15.1901 7.71648L10.3333 3.34539Z"
      />
    </svg>
  );
}

export function DetailPreviewCard({ label, content }: DetailPreviewCardProps) {
  const { t } = useTranslation();

  return (
    <PreviewCard.Root>
      <PreviewTrigger>
        {t('Events.PreviewCard.View')} {label}
      </PreviewTrigger>
      <PreviewCard.Portal>
        <PreviewCard.Positioner sideOffset={8}>
          <PreviewPopup>
            <Arrow>
              <ArrowSvg />
            </Arrow>
            {content}
          </PreviewPopup>
        </PreviewCard.Positioner>
      </PreviewCard.Portal>
    </PreviewCard.Root>
  );
}
